import { type ColumnFiltersState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'

interface UsePersistentColumnFiltersOptions<T = ColumnFiltersState> {
  key: string
  initialColumnFilters?: T
}

export function usePersistentColumnFilters<T = ColumnFiltersState>({
  key,
  initialColumnFilters = [] as T,
}: UsePersistentColumnFiltersOptions<T>) {
  const [columnFilters, setColumnFilters] = useState<T>(() => {
    // Try to load from localStorage on initialization
    try {
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored)
        return Array.isArray(parsed) ? (parsed as T) : initialColumnFilters
      }
    } catch (error) {
      console.warn('Failed to load filtering state from localStorage:', error)
    }
    return initialColumnFilters
  })

  // Save to localStorage whenever filtering changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(columnFilters))
    } catch (error) {
      console.warn('Failed to save filtering state to localStorage:', error)
    }
  }, [key, columnFilters])

  return [columnFilters, setColumnFilters] as const
}

// Utility function to clear persistent filtering state
export function clearPersistentColumnFilters(key: string) {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to clear filtering state from localStorage:', error)
  }
}
